import { useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import type { SessionEvent } from '@/types';
import {
  CATEGORY_STAT_LABELS,
  JUDGEMENT_STAT_COLORS,
  JUDGEMENT_STAT_LABELS,
  computeLiveEventStats,
  type JudgementStatKey,
} from '@/components/live/liveEventStats';

interface Props {
  events: SessionEvent[];
  inverted?: boolean;
}

function StatBar({
  label,
  count,
  total,
  color,
  inverted,
  showPercent = true,
}: {
  label: string;
  count: number;
  total: number;
  color: string;
  inverted?: boolean;
  showPercent?: boolean;
}) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  const muted = inverted ? 'rgba(255,255,255,0.55)' : 'text.secondary';
  const primary = inverted ? '#fff' : undefined;
  return (
    <Box sx={{ mb: 1.25, minWidth: 0 }}>
      <Stack direction="row" justifyContent="space-between" gap={1} sx={{ mb: 0.35 }}>
        <Typography variant="body2" fontWeight={600} sx={{ color: primary, minWidth: 0 }}>
          {label}
        </Typography>
        <Typography variant="body2" sx={{ color: muted, flexShrink: 0 }}>
          {showPercent ? `${count} (${pct}%)` : count}
        </Typography>
      </Stack>
      <LinearProgress
        variant="determinate"
        value={showPercent ? pct : total > 0 ? Math.min(100, (count / total) * 100) : 0}
        sx={{
          height: 8,
          borderRadius: 999,
          bgcolor: inverted ? 'rgba(255,255,255,0.1)' : 'action.hover',
          '& .MuiLinearProgress-bar': { bgcolor: color, borderRadius: 999 },
        }}
      />
    </Box>
  );
}

export default function LiveEventSummaryPanel({ events, inverted = false }: Props) {
  const stats = useMemo(() => computeLiveEventStats(events), [events]);

  const muted = inverted ? 'rgba(255,255,255,0.55)' : 'text.secondary';
  const heading = inverted ? 'rgba(255,255,255,0.9)' : 'text.primary';
  const cardSx = inverted
    ? {
        borderColor: 'rgba(255,255,255,0.14)',
        bgcolor: 'rgba(255,255,255,0.04)',
        color: '#fff',
      }
    : undefined;
  const tableSx = {
    width: '100%',
    tableLayout: 'fixed' as const,
    ...(inverted
      ? {
          '& .MuiTableCell-root': { color: 'rgba(255,255,255,0.85)', borderColor: 'rgba(255,255,255,0.1)' },
        }
      : {}),
  };
  const tableCellSx = { wordBreak: 'break-word' as const, px: { xs: 0.75, sm: 1.25 } };

  if (events.length === 0) {
    return (
      <Box sx={{ py: 4, textAlign: 'center', color: muted }}>
        <Typography>尚無統計資料</Typography>
      </Box>
    );
  }

  const judgementTotal = stats.total;
  const judgementKeys = (Object.keys(JUDGEMENT_STAT_LABELS) as JudgementStatKey[]).filter(
    (key) => stats.judgement[key] > 0
  );
  const categoryKeys = (Object.keys(CATEGORY_STAT_LABELS) as Array<keyof typeof CATEGORY_STAT_LABELS>).filter(
    (key) => stats.category[key] > 0
  );
  const categoryMax = Math.max(stats.total, ...categoryKeys.map((k) => stats.category[k]), 1);

  const kpiCards = [
    { label: '總標記', value: String(stats.total) },
    {
      label: '正確率',
      value: `${Math.round((stats.judgement.correct / Math.max(1, stats.total)) * 100)}%`,
    },
    { label: '錯誤', value: String(stats.judgement.wrong) },
    { label: '灰色 / Missing', value: String(stats.judgement.uncertain + stats.judgement.missing) },
  ];

  const categoryColor = (key: keyof typeof CATEGORY_STAT_LABELS) =>
    key === 'flow' ? '#1565c0' : key === 'discipline' ? '#c62828' : key === 'technical' ? '#ef6c00' : '#757575';

  return (
    <Stack spacing={2} sx={{ minWidth: 0, overflowX: 'hidden' }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
          gap: 1.25,
          minWidth: 0,
        }}
      >
        {kpiCards.map((kpi) => (
          <Card key={kpi.label} variant="outlined" sx={{ ...cardSx, height: '100%', minWidth: 0 }}>
            <CardContent sx={{ py: 1.5, px: { xs: 1, sm: 1.5 }, '&:last-child': { pb: 1.5 }, textAlign: 'center' }}>
              <Typography variant="h5" fontWeight={800} sx={{ fontSize: { xs: '1.1rem', sm: '1.5rem' } }}>
                {kpi.value}
              </Typography>
              <Typography variant="caption" sx={{ color: muted, lineHeight: 1.2 }}>
                {kpi.label}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' },
          gap: 2,
          minWidth: 0,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle2" fontWeight={800} gutterBottom sx={{ color: heading }}>
            判決評價分佈
          </Typography>
          {judgementKeys.length === 0 ? (
            <Typography variant="body2" sx={{ color: muted }}>
              尚無判決資料
            </Typography>
          ) : (
            judgementKeys.map((key) => (
              <StatBar
                key={key}
                label={JUDGEMENT_STAT_LABELS[key]}
                count={stats.judgement[key]}
                total={judgementTotal}
                color={JUDGEMENT_STAT_COLORS[key]}
                inverted={inverted}
              />
            ))
          )}
        </Box>

        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle2" fontWeight={800} gutterBottom sx={{ color: heading }}>
            事件類別分佈
          </Typography>
          {categoryKeys.length === 0 ? (
            <Typography variant="body2" sx={{ color: muted }}>
              尚無類別資料
            </Typography>
          ) : (
            categoryKeys.map((key) => (
              <StatBar
                key={key}
                label={CATEGORY_STAT_LABELS[key]}
                count={stats.category[key]}
                total={categoryMax}
                color={categoryColor(key)}
                inverted={inverted}
                showPercent={false}
              />
            ))
          )}
        </Box>
      </Box>

      {stats.byPeriod.length > 0 && (
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle2" fontWeight={800} gutterBottom sx={{ color: heading }}>
            各節次標記數
          </Typography>
          <Table size="small" sx={tableSx}>
            <TableHead>
              <TableRow>
                <TableCell sx={tableCellSx}>節次</TableCell>
                <TableCell align="right" sx={{ ...tableCellSx, width: '22%' }}>
                  數量
                </TableCell>
                <TableCell align="right" sx={{ ...tableCellSx, width: '22%' }}>
                  佔比
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {stats.byPeriod.map((row) => (
                <TableRow key={row.period}>
                  <TableCell sx={tableCellSx}>{row.label}</TableCell>
                  <TableCell align="right" sx={tableCellSx}>
                    {row.count}
                  </TableCell>
                  <TableCell align="right" sx={tableCellSx}>
                    {Math.round((row.count / stats.total) * 100)}%
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      {stats.topEventTypes.length > 0 && (
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle2" fontWeight={800} gutterBottom sx={{ color: heading }}>
            常見事件類型
          </Typography>
          <Table size="small" sx={tableSx}>
            <TableHead>
              <TableRow>
                <TableCell sx={tableCellSx}>事件</TableCell>
                <TableCell align="right" sx={{ ...tableCellSx, width: '22%' }}>
                  次數
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {stats.topEventTypes.map((row) => (
                <TableRow key={row.key}>
                  <TableCell sx={tableCellSx}>{row.label}</TableCell>
                  <TableCell align="right" sx={tableCellSx}>
                    {row.count}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </Stack>
  );
}
